<template>
  <form-layout key="project-selection" title="Add to project" @on-dismiss="onDismiss">
    <project-selection-form
      :job-id="jobId"
      :require-user-confirmation="false"
      :is-bulk-update="false"
      @dismiss="onDismiss"
      @project-created="onProjectCreated"
      @project-selected="onProjectSelected"
      @job-added-to-project="onJobAddedToProject"
    />

    <vs-snackbar v-if="hasError" v-model="error" :type="error.value.type" sticky>
      {{ error.value.message }}
    </vs-snackbar>
  </form-layout>
</template>

<script setup lang="ts">
import FormLayout from '../common/FormLayout.vue'
import { computed, provide, Ref, ref } from 'vue'
import { useJobBottomSheetStatesStore } from '../jobs/jobBottomSheetStatesStore'
import { ProjectSelectionMessage } from './ProjectSelectionMessage'
import ProjectSelectionForm from './ProjectSelectionForm.vue'

// ---------------------------
//  Props
// ---------------------------

const props = defineProps<{
  jobId: number
  ownerId: string
}>()

// ---------------------------
//  Events
// ---------------------------

const emit = defineEmits(['job-added-success', 'job-added-failure', 'dismiss', 'error'])

// ---------------------------
//  Composables
// ---------------------------

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

// ---------------------------
//  Refs
// ---------------------------

const error: Ref<ProjectSelectionMessage | null> = ref(null)

// ---------------------------
//  Computed
// ---------------------------

const hasError: Ref<boolean> = computed(() => error.value !== null)

// ---------------------------
//  Provides
// ---------------------------

provide('ownerId', ref(props.ownerId))

// ---------------------------
//  Methods
// ---------------------------

const onDismiss = () => {
  emit('dismiss')
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isProjectBottomSheetVisible: false,
  })
}

// ---------------------------
//  Event s
// ---------------------------

const onProjectCreated = (obj: ProjectSelectionMessage) => {
  // Display snackbar message on error.
  if (obj.type === 'error') {
    error.value = obj
  }
}

const onProjectSelected = (obj: ProjectSelectionMessage) => {
  // Display snackbar message on error.
  if (obj.type === 'error') {
    error.value = obj
  }
}

const onJobAddedToProject = (obj: ProjectSelectionMessage) => {
  if (obj.type === 'error') {
    emit('job-added-failure', 'addToExistingProjectError')
    return
  }

  // Emit this specific event so that the project is opened.
  emit('job-added-success', obj.projectId)
}
</script>

<style scoped>
.project-divider::after {
  content: attr(aria-label);
  font-family: var(--font-regular), serif;
  font-size: 12px;
  text-transform: uppercase;
  position: absolute;

  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0 var(--space-smaller);
  background-color: white;
}
.project-divider {
  position: relative;
  margin: var(--space-large) 0 0 0;
  padding: 0 0 var(--space-large) 0;
}
</style>
